import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useB2BMode } from '../hooks/useB2BMode';
import { apiFetch } from '../services/apiClient';
import logoMark from '../logo/Symbol_32.svg';
import logoFull from '../logo/Logo_32.svg';
import loginFeature1 from '../assets/images/login_feature1.svg';
import loginFeature2 from '../assets/images/login_feature2.png';
import loginFeature3 from '../assets/images/login_feature3.svg';
import loginFeature4 from '../assets/images/login_feature4.svg';
import './IndividualLoginPage.scss';

const IndividualLoginPage = () => {
  const [googleClientId, setGoogleClientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { loginUser } = useAppContext();
  const { enableB2BMode } = useB2BMode();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_expired') === 'true') {
      setError('세션이 만료되었습니다. 다시 로그인해주세요.');
    }

    apiFetch('/auth/google/config', { method: 'GET' })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setGoogleClientId(data.google_client_id);
        } else {
          console.error('구글 설정 실패:', data.error);
          setError('구글 OAuth 설정을 불러오지 못했습니다.');
        }
      })
      .catch((err) => {
        console.error('구글 설정 로드 실패:', err);
        setError('구글 OAuth 설정을 불러오지 못했습니다.');
      });
  }, []);

  const handleLoginSuccess = (userData) => {
    loginUser(userData, null);

    if (userData.password_reset_required) {
      navigate('/change-password?required=true', { replace: true });
      return;
    }

    const isSuperAdmin = userData.tier === 'super';
    const isEnterprise = userData.account_type === 'business' && userData.tier === 'enterprise';
    if (isSuperAdmin) {
      navigate('/super-admin');
    } else if (isEnterprise) {
      enableB2BMode();
      navigate('/b2b');
    } else {
      enableB2BMode();
      navigate('/b2b');
    }
  };

  const verifyGoogleToken = async (token) => {
    try {
      const response = await apiFetch('/auth/google/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (data.success) {
        handleLoginSuccess(data.user);
      } else {
        setError(data.error || '로그인에 실패했습니다.');
      }
    } catch (err) {
      console.error('일반 회원 구글 로그인 실패:', err);
      setError('로그인 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!googleClientId) {
      setError('구글 OAuth 설정이 아직 로드되지 않았습니다.');
      return;
    }

    setLoading(true);
    setError('');

    const redirectUri = `${window.location.origin}/auth/callback`;
    const popup = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${encodeURIComponent(
        redirectUri,
      )}&response_type=id_token&scope=openid%20email%20profile&nonce=${Date.now()}`,
      'googleAuth',
      'width=500,height=600,scrollbars=yes,resizable=yes',
    );

    if (!popup) {
      setError('팝업이 차단되었습니다. 브라우저 팝업 허용 후 다시 시도해주세요.');
      setLoading(false);
      return;
    }

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', messageListener);
        setLoading(false);
      }
    }, 1000);

    const messageListener = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
        clearInterval(checkClosed);
        popup.close();
        window.removeEventListener('message', messageListener);
        verifyGoogleToken(event.data.token);
      } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
        clearInterval(checkClosed);
        popup.close();
        window.removeEventListener('message', messageListener);
        setError('구글 로그인에 실패했습니다.');
        setLoading(false);
      }
    };

    window.addEventListener('message', messageListener);
  };

  return (
    <div className="login-page individual-login-page">
      <div className="login-container">
        <div className="login-left">
          <div className="login-content individual-login-content">
            <div className="login-header">
              <img src={logoFull} alt="ReOpsAI" className="login-logo" />
            </div>

            {/* <div className="individual-login-copy">
              <h1>로그인</h1>
            </div> */}

            {error && <div className="error-message individual-login-error">{error}</div>}

            <button
              type="button"
              className="individual-google-login-btn"
              onClick={handleGoogleLogin}
              disabled={loading || !googleClientId}
            >
              {loading ? (
                <span>로그인 중...</span>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span>Google 계정으로 무료 체험하기</span>
                </>
              )}
            </button>

            <p className="individual-login-enterprise-link">
              팀·기업 계정이신가요? <Link to="/login">기업 로그인</Link>
            </p>
          </div>
        </div>

        <div className="login-right">
          <div className="login-right-bg-shape login-right-bg-shape-top" />
          <div className="login-right-bg-shape login-right-bg-shape-bottom" />
          <div className="login-showcase">
            <div className="login-showcase-header">
              <img src={logoMark} alt="" className="login-showcase-logo" />
              <h2>ReOps와 함께<br />가장 빠르고 깊이 있는 리서치를 경험하세요.</h2>
              <p>복잡한 리서치 설계부터 AI 퍼소나 검증까지,<br />ReOps AI가 인사이트 발굴의 전 과정을 돕습니다.</p>
            </div>

            <div className="login-feature-grid" aria-label="ReOpsAI 주요 기능">
              <div className="login-feature-card">
                <img
                  src={loginFeature1}
                  alt="인터뷰 설계부터 가이드라인까지 한 번에 생성하는 리서치 계획"
                  className="login-feature-card-image"
                  loading="eager"
                />
              </div>
              <div className="login-feature-card">
                <img
                  src={loginFeature2}
                  alt="UX 태도와 도메인 DNA를 반영한 맞춤형 AI 페르소나 생성"
                  className="login-feature-card-image"
                  loading="eager"
                />
              </div>
              <div className="login-feature-card">
                <img
                  src={loginFeature3}
                  alt="최적화된 AI 퍼소나를 통한 빠른 화면 사용성 검증"
                  className="login-feature-card-image"
                  loading="eager"
                />
              </div>
              <div className="login-feature-card">
                <img
                  src={loginFeature4}
                  alt="AI 퍼소나의 일관된 응답을 통해 인사이트 찾는 1:1 인터뷰"
                  className="login-feature-card-image"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndividualLoginPage;
